/**
 * Configure Card Component for Product Customization
 * 
 * An interactive card component that presents the bike configurator feature
 * with engaging hover effects and clear call-to-action messaging.
 * Currently displays "Coming Soon" messaging for the configurator.
 * 
 * Features:
 * - Responsive design with different layouts for mobile/desktop
 * - Hover-activated state transitions with opacity effects
 * - Background image integration with fallback support
 * - Scale animations for enhanced visual feedback
 * - Responsive typography and spacing
 * 
 * Visual States:
 * - Default: Shows title and configure button
 * - Hover: Reveals "Coming Soon" overlay with development message
 * - Mobile: Simplified layout optimized for touch devices
 * 
 * Image Handling:
 * - Dynamic background from Sanity CMS or fallback
 * - Responsive image sizing with object-cover
 * - Alt text from CMS or descriptive defaults
 * - Next.js Image optimization with fill layout
 * 
 * User Experience:
 * - Clear messaging about configurator development status
 * - Engaging animations to maintain user interest
 * - Accessible button styling and interactions
 * - Professional "coming soon" presentation
 * 
 * @param {string} title - Card title (default: "Make it Yours")
 * @param {string} buttonText - Button label (default: "Configure")
 * @param {string} productLink - URL for configuration flow (future use)
 * @param {object} backgroundImage - Sanity image object for card background
 * @param {string} defaultImage - Fallback image path
 * 
 * @description Interactive configuration card with coming soon messaging and hover effects
 */

import Image from 'next/image';
import Link from 'next/link';
import { urlForImage } from '../../src/app/studio/lib/image';
import { sanityLoader } from '../../utils/sanityLoader';
import { useTranslation } from '../../src/hooks/useTranslation';

const ConfigureCard = ({ 
    title, 
    buttonText, 
    productLink, 
    backgroundImage, 
    defaultImage = "/images/road-makeItYours.jpeg" 
  }) => {
    const { t } = useTranslation();
    return (
    <div className="relative overflow-hidden rounded-lg border border-wood-brown/20 h-full w-full group transition-all duration-500 hover:shadow-xl">
      {/* Desktop layout - with image filling entire height */}
      <div className="hidden sm:block h-full w-full relative">
        <div className="absolute inset-0 w-full h-full transition-transform duration-700 overflow-hidden">
          <Image
            src={backgroundImage ? urlForImage(backgroundImage) : defaultImage}
            alt={backgroundImage?.alt || title || "Configure Your Bike"}
            fill
            loader={sanityLoader}
            sizes="50vw"
            className="object-cover transition-transform duration-700 group-hover:scale-105"
          />
        </div>
        
        {/* Default state */}
        <div className="absolute inset-0 bg-black bg-opacity-30 flex flex-col items-center justify-between p-4 transition-opacity duration-300 group-hover:opacity-0">
          <h3 className="text-white text-lg md:text-2xl uppercase font-bold">
            {title || t('configure.title')} 
          </h3>
          <div className="px-4 py-2 bg-white text-black font-bold rounded-md transition duration-300 ease-in-out mt-auto">
            {buttonText || t('configure.button_text')}
          </div>
        </div>
        
        {/* Coming Soon overlay - visible on hover */}
        <div className="absolute inset-0 bg-black bg-opacity-80 flex flex-col items-center justify-center p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <div className="flex flex-col items-center gap-4">
            <span className="text-accent-color text-sm font-bold tracking-wider uppercase">{t('configure.coming_soon')}</span>
            <h3 className="text-white text-xl md:text-2xl font-bold text-center mb-2">
              {t('configure.coming_soon_description')}
            </h3>
            <p className="text-gray-300 text-center mb-6 max-w-xs">
              {t('configure.coming_soon_description_2')}
            </p>
            <Link 
              href="/contact" 
              className="px-6 py-3 bg-accent-color text-white font-bold rounded-md hover:bg-orange-600 transition duration-300 ease-in-out transform hover:scale-105"
            >
              {t('configure.contact_us')}
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile layout - keeping the improved taller version */}
      <div className="sm:hidden block h-full min-h-[350px] w-full relative">
        {/* Full height image */}
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <Image
            src={backgroundImage ? urlForImage(backgroundImage) : defaultImage}
            alt={backgroundImage?.alt || title || "Configure Your Bike"}
            fill
            loader={sanityLoader}
            sizes="100vw"
            className="object-cover"
          />
        </div>
        
        {/* Default state - Make It Yours */}
        <div className="absolute inset-0 bg-black bg-opacity-30 flex flex-col items-center justify-between p-4 transition-opacity duration-300 group-hover:opacity-0">
          <h3 className="text-white text-lg uppercase font-bold">
            {t('configure.title')}
          </h3>
          <div className="px-4 py-2 bg-white text-black font-bold rounded-md transition duration-300 ease-in-out mt-auto">
            {t('configure.button_text')}
          </div>
        </div>
        
        {/* Coming Soon overlay - visible on hover */}
        <div className="absolute inset-0 bg-black bg-opacity-80 flex flex-col items-center justify-center p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <div className="flex flex-col items-center gap-3">
            <span className="text-accent-color text-sm font-bold tracking-wider uppercase">{t('configure.coming_soon')}</span>
            <h3 className="text-white text-xl font-bold text-center mb-2">
              {t('configure.coming_soon_description')}
            </h3>
            <p className="text-gray-300 text-center mb-4 max-w-xs text-sm">
              {t('configure.coming_soon_description_2')}
            </p>
            <Link 
              href="/contact" 
              className="px-5 py-2 bg-accent-color text-white font-bold rounded-md hover:bg-orange-600 transition duration-300 ease-in-out transform hover:scale-105 text-sm"
            >
              {t('configure.contact_us')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConfigureCard;



// Here I will define how the configurator card will behave
// First of all define path
// Create and define page, link correctly with button (when user clicks on configurator)

// A new page will then execute with the structure and content predefined. 
// The new page content is uploaded mainly through sanity studio 

